import React, { useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  FieldArrayRenderProps,
  FieldRenderProps,
} from "@progress/kendo-react-form";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { MdDelete } from "react-icons/md";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { IItem } from "../item/itemModel";
import { deleteDullOrderItems } from "./services/dull.services";
import {
  openDullOrderSelectionDialog,
  setDullOrderItemIndex,
} from "./dullSlice";
import { IProcess } from "../process/processModel";
import FormTextArea from "../../components/formFields/FormTextArea";
import DullOrderSelectionDialog from "./DullOrderSelectionDialog";
import { FaRegEye } from "react-icons/fa";
import IconButton from "../../components/common/IconButton";
import { findProductImageByProcess } from "../item/services/item.services";
import { LoadingPanel } from "../../components/layout/Loading";
import { ImCross } from "react-icons/im";
import AlertBox from "../../components/common/AlertBox";
import Slider from "react-slick";
import { PROCESS_ID } from "../../_contstants/common";
import { FetchWeight } from "../palish/services/palish.services";

const DullOrderItemArray = (fieldArrayRenderProps: FieldArrayRenderProps) => {
  const dispatch = useAppDispatch();
  const DullOrderItemIndex = useAppSelector(
    (state) => state.dull.DullOrderItemIndex,
  );
  const name = fieldArrayRenderProps?.name;
  const ItemList = useAppSelector((state) => state.item.ItemList);
  const ProcessList = useAppSelector((state) => state.Process.ProcessList);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const handleDeleteAction = React.useCallback(
    async (index: any) => {
      if (fieldArrayRenderProps?.value[index].id) {
        const response = await dispatch(
          deleteDullOrderItems(fieldArrayRenderProps?.value[index].id),
        );
        if (response.meta.requestStatus === "fulfilled") {
          fieldArrayRenderProps.onRemove({ index: index });
          dispatch(closeDialog());
          dispatch(setDullOrderItemIndex(-1));
        }
      } else {
        fieldArrayRenderProps.onRemove({ index: index });
        dispatch(closeDialog());
        dispatch(setDullOrderItemIndex(-1));
      }
    },
    [fieldArrayRenderProps],
  );
  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps?.onPush({
      value: {
        previous_process_id: null,
        order_id: null,
        item_id: null,
        item_description: "",
        weight: null,
        remarks: "",
      },
    });
  }, [fieldArrayRenderProps]);

  const handleOpenDeleteDialog = (index: any) => {
    dispatch(openDialog("DullOrderDeleteDialog"));
    dispatch(setDullOrderItemIndex(index));
  };

  const handleSelectOrder = (index: number) => {
    dispatch(openDullOrderSelectionDialog());
    dispatch(setDullOrderItemIndex(index));
  };

  const handleviewImage = async (item_id: any) => {
    const payload = {
      item_id: item_id,
      process_id: PROCESS_ID.dull,
    };
    const res = await dispatch(findProductImageByProcess(payload));
    if (res.meta.requestStatus === "fulfilled") {
      dispatch(openDialog("show-process-wise-image"));
    }
  };

  const karigar_id =
    fieldArrayRenderProps.formRenderProps.valueGetter("karigar_id");
  const dull_issue_order =
    fieldArrayRenderProps.formRenderProps.valueGetter("dull_issue_order");

  const handlefetchWeight = async (
    previous_process_id: number,
    order_id: number,
    index: number,
  ) => {
    const payload = {
      process_id: previous_process_id,
      order_id: order_id,
      karigar_id: karigar_id,
    };

    const res = await dispatch(FetchWeight(payload));
    if (res.meta.requestStatus === "fulfilled") {
      fieldArrayRenderProps.formRenderProps.onChange(
        `dull_issue_order.${index}.weight`,
        {
          value: res.payload.Data[0].gross_weight || 0,
        },
      );
    } else {
      fieldArrayRenderProps.formRenderProps.onChange(
        `dull_issue_order.${index}.weight`,
        {
          value: 0,
        },
      );
    }
  };

  const handlePalishOrderChange = (
    index: number,
    field: string,
    value: any,
  ) => {
    fieldArrayRenderProps.formRenderProps.onChange(
      `dull_issue_order.${index}.${field}`,
      { value },
    );
    if ((field === "previous_process_id" || field === "order_id") && value) {
      const currentItem = dull_issue_order?.[index];
      const hasPreviousProcessId =
        field === "previous_process_id"
          ? value
          : currentItem?.previous_process_id;

      const hasOrderId = field === "order_id" ? value : currentItem?.order_id;

      if (hasPreviousProcessId && hasOrderId) {
        handlefetchWeight(hasPreviousProcessId, hasOrderId, index);
      }
    }
  };

  const OrderNoField = (props: FieldRenderProps) => {
    const { value } = props;
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "18px",
        }}
      >
        {value}
      </div>
    );
  };

  const orderTableColumns = [
    { width: "15%" }, // Previous Process
    { width: "10%" }, // Select Order
    { width: "10%" }, // Order No
    { width: "15%" }, // Product Name
    { width: "25%" }, // Description
    { width: "15%" }, // Weight (gm)
    { width: "10%" }, // Actions
  ];

  return (
    <>
      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 && (
          <GridLayout
            cols={[{ width: "100%" }]}
            style={{ overflowX: "scroll", scrollbarWidth: "thin" }}
          >
            {dialogName === "DullOrderDeleteDialog" && (
              <AppDialog>
                <>
                  <Typography.h5>{"Delete Order"}</Typography.h5>
                  <GridLayout>
                    <GridLayoutItem>
                      <Typography.p>
                        Are you sure you want to delete this?
                      </Typography.p>
                    </GridLayoutItem>
                    <GridLayoutItem
                      style={{
                        display: "flex",
                        justifyContent: "end",
                      }}
                    >
                      <DialogCloseButton themeColor="error" />
                      <Button
                        fillMode={"solid"}
                        themeColor={"error"}
                        onClick={() => handleDeleteAction(DullOrderItemIndex)}
                        type="button"
                      >
                        Delete
                      </Button>
                    </GridLayoutItem>
                  </GridLayout>
                </>
              </AppDialog>
            )}
            <GridLayoutItem
              className="bg-dark text-white fw-600"
              style={{ minWidth: 1500 }}
            >
              <GridLayout
                style={{
                  marginRight: 60,
                  padding: 10,
                }}
                gap={{ rows: 0, cols: 10 }}
                cols={orderTableColumns}
              >
                <GridLayoutItem>Previous Process</GridLayoutItem>
                <GridLayoutItem>Select Order</GridLayoutItem>
                <GridLayoutItem>Order No</GridLayoutItem>
                <GridLayoutItem>Product Name</GridLayoutItem>
                <GridLayoutItem>Description</GridLayoutItem>
                <GridLayoutItem>Weight (gm)</GridLayoutItem>
                {/* <GridLayoutItem>Remarks</GridLayoutItem> */}
                {/* <GridLayoutItem>Image</GridLayoutItem> */}
                <GridLayoutItem
                  style={{
                    position: "sticky",
                    top: 0,
                    right: 0,
                    zIndex: 9,
                    background: "#212529",
                    textAlign: "end",
                  }}
                ></GridLayoutItem>
              </GridLayout>
            </GridLayoutItem>

            <DullOrderSelectionDialog
              fieldArrayRenderProps={fieldArrayRenderProps}
            />
            <ShowProcessWiseImageDialog />
            {fieldArrayRenderProps.value &&
              fieldArrayRenderProps.value.length > 0 &&
              fieldArrayRenderProps.value.map(
                (dull_issue_order: any, index: number) => (
                  <GridLayoutItem key={index} style={{ minWidth: 1500 }}>
                    <GridLayout
                      style={{ marginRight: 60 }}
                      gap={{ rows: 0, cols: 10 }}
                      cols={orderTableColumns}
                    >
                      <GridLayoutItem>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${name}.${index}.previous_process_id`}
                          placeholder="Select  Previous Process"
                          component={FormSelectionField}
                          onChange={(e) =>
                            e &&
                            e?.value &&
                            handlePalishOrderChange(
                              index,
                              "previous_process_id",
                              e.value,
                            )
                          }
                          options={ProcessList?.map((process: IProcess) => {
                            return {
                              value: process.ID,
                              label: process.process_name,
                            };
                          })}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Button
                          style={{
                            marginTop: 5,
                          }}
                          onClick={() => handleSelectOrder(index)}
                          fillMode={"solid"}
                          disabled={
                            !fieldArrayRenderProps.formRenderProps.valueGetter(
                              "karigar_id",
                            )
                          }
                          themeColor={"success"}
                          type="button"
                          size={"small"}
                        >
                          Select Order
                        </Button>
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${name}.${index}.order_no`}
                          rows={1}
                          placeholder="Order No"
                          component={OrderNoField}
                          disabled={true}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${name}.${index}.item_id`}
                          // label="Product Name"
                          disabled={true}
                          placeholder="Select Product"
                          component={FormSelectionField}
                          validator={requiredValidator}
                          options={ItemList?.map((item: IItem) => {
                            return {
                              value: item.id,
                              label: item.item_name,
                            };
                          })}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${name}.${index}.item_description`}
                          rows={1}
                          placeholder="Write Here..."
                          component={FormTextArea}
                        />
                      </GridLayoutItem>
                      <GridLayoutItem>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${name}.${index}.weight`}
                          placeholder="0.00"
                          type="number"
                          format="n3"
                          component={FormNumericTextField}
                          validator={requiredValidator}
                        />
                      </GridLayoutItem>
                      {/* <GridLayoutItem>
                        <Field
                          wrapperStyle={{ margin: 0 }}
                          name={`${name}.${index}.remarks`}
                          rows={1}
                          placeholder="Write Here..."
                          component={FormTextArea}
                        />
                      </GridLayoutItem> */}

                      <GridLayoutItem
                        style={{
                          position: "sticky",
                          top: 0,
                          right: 0,
                          zIndex: 9,
                          pointerEvents: "none",
                        }}
                      >
                        <IconButton
                          onClick={() =>
                            handleviewImage(dull_issue_order.item_id)
                          }
                          disabled={!dull_issue_order.item_id}
                          themeColor={"info"}
                          size={"small"}
                          fillMode={"solid"}
                          type="button"
                          title="View Images"
                          style={{
                            position: "absolute",
                            height: 32,
                            width: 32,
                            top: 10,
                            right: 60,
                            pointerEvents: "all",
                          }}
                        >
                          <FaRegEye
                            className="absolute-position"
                            style={{ fontSize: "16px" }}
                          />
                        </IconButton>
                        <IconButton
                          onClick={() => handleOpenDeleteDialog(index)}
                          themeColor={"error"}
                          size={"small"}
                          fillMode={"solid"}
                          type="button"
                          title="Delete Order"
                          style={{
                            position: "absolute",
                            height: 32,
                            width: 32,
                            top: 10,
                            right: 20,
                            pointerEvents: "all",
                          }}
                        >
                          <MdDelete
                            className="absolute-position"
                            style={{ fontSize: "16px" }}
                          />
                        </IconButton>
                      </GridLayoutItem>
                    </GridLayout>
                  </GridLayoutItem>
                ),
              )}
          </GridLayout>
        )}
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{ marginBottom: 10, marginTop: 10, textAlign: "left" }}
        >
          <Button
            fillMode={"solid"}
            themeColor={"primary"}
            disabled={
              !fieldArrayRenderProps.formRenderProps.valueGetter("karigar_id")
            }
            onClick={pushElementInarray}
            type="button"
          >
            Add Order
          </Button>
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const ShowProcessWiseImageDialog: React.FC = () => {
  const settings = {
    dots: true,
    infinite: true,
    autoplay: false,
    // centerMode: true,
    autoplaySpeed: 2000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    centerPadding: "0px",
    centerMargin: "0px",
    draggable: true,
    responsive: [
      {
        breakpoint: 991,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const loading = useAppSelector((state) => state.item.loading);
  const ProcessByImageList = useAppSelector(
    (state) => state.item.ProcessByImageList,
  );
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const handleOpenImage = (url: string) => {
    if (url && typeof url === "string") {
      window.open(url);
    }
  };

  return (
    <>
      {dialogName === "show-process-wise-image" && (
        <AppDialog>
          {loading ? (
            <div>
              <LoadingPanel gridRef={gridRef} />
            </div>
          ) : (
            <div style={{ width: "40vw", height: "50vh" }}>
              <GridLayout
                style={{
                  marginLeft: 10,
                }}
                gap={{ rows: 10, cols: 10 }}
              >
                <GridLayoutItem
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography.h4 style={{ margin: "0" }}>
                    Process Images
                  </Typography.h4>
                  <ImCross
                    style={{
                      color: "white",
                      background: "red",
                      borderRadius: "50%",
                      padding: "5px",
                      fontSize: "24px",
                    }}
                    onClick={() => dispatch(closeDialog())}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  {ProcessByImageList && ProcessByImageList?.length === 0 && (
                    <AlertBox alert="No Image Available!" />
                  )}
                </GridLayoutItem>
                <GridLayoutItem
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <div
                    className="slider-container product-view-images"
                    style={{ width: "30vw" }}
                  >
                    {ProcessByImageList && ProcessByImageList.length > 1 ? (
                      <Slider {...settings}>
                        {ProcessByImageList &&
                          ProcessByImageList.length > 0 &&
                          ProcessByImageList.map(
                            (image: any, index: number) => (
                              <div>
                                <div key={index} style={{ height: 300 }}>
                                  <img
                                    src={image.image_name}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                    }}
                                    onClick={() =>
                                      handleOpenImage(image.image_name)
                                    }
                                  />
                                </div>
                                {/* <Typography.h5 style={{ textAlign: "center" }}>
                                {image.title}
                              </Typography.h5> */}
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 5,
                                  }}
                                >
                                  <div
                                    style={{
                                      background: "#e8e8e8",
                                      padding: "5px 10px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <p
                                      style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "14px",
                                        color: "#718096",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Title
                                    </p>
                                    <p
                                      style={{
                                        margin: 0,
                                        fontSize: "16px",
                                        color: "#2d3748",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {image.title || "N/A"}
                                    </p>
                                  </div>
                                  <div
                                    style={{
                                      background: "#e8e8e8",
                                      padding: "5px 10px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <p
                                      style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "14px",
                                        color: "#718096",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Process Name
                                    </p>
                                    <p
                                      style={{
                                        margin: 0,
                                        fontSize: "16px",
                                        color: "#2d3748",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {image.process_name || "N/A"}
                                    </p>
                                  </div>
                                  <div
                                    style={{
                                      background: "#e8e8e8",
                                      padding: "5px 10px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <p
                                      style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "14px",
                                        color: "#718096",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Main Image
                                    </p>
                                    <p
                                      style={{
                                        margin: 0,
                                        fontSize: "16px",
                                        color: "#2d3748",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {image.isreadyimage ? "YES" : "NO"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                      </Slider>
                    ) : (
                      <>
                        {ProcessByImageList &&
                          ProcessByImageList.length > 0 &&
                          ProcessByImageList.map(
                            (image: any, index: number) => (
                              // <div
                              //   style={{
                              //     width: "40%",
                              //     margin: "auto",
                              //   }}
                              //   key={index}
                              // >
                              //   <img
                              //     src={image.image_name}
                              //     style={{
                              //       width: "100%",
                              //       height: "auto",
                              //       objectFit: "contain",
                              //     }}
                              //     onClick={() =>
                              //       handleOpenImage(image.image_name)
                              //     }
                              //   />
                              // </div>
                              <div>
                                <div key={index} style={{ height: 300 }}>
                                  <img
                                    src={image.image_name}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                    }}
                                    onClick={() =>
                                      handleOpenImage(image.image_name)
                                    }
                                  />
                                </div>
                                {/* <Typography.h5 style={{ textAlign: "center" }}>
                               {image.title}
                             </Typography.h5> */}
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 5,
                                  }}
                                >
                                  <div
                                    style={{
                                      background: "#e8e8e8",
                                      padding: "5px 10px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <p
                                      style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "14px",
                                        color: "#718096",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Title
                                    </p>
                                    <p
                                      style={{
                                        margin: 0,
                                        fontSize: "16px",
                                        color: "#2d3748",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {image.title || "N/A"}
                                    </p>
                                  </div>
                                  <div
                                    style={{
                                      background: "#e8e8e8",
                                      padding: "5px 10px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <p
                                      style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "14px",
                                        color: "#718096",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Process Name
                                    </p>
                                    <p
                                      style={{
                                        margin: 0,
                                        fontSize: "16px",
                                        color: "#2d3748",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {image.process_name || "N/A"}
                                    </p>
                                  </div>
                                  <div
                                    style={{
                                      background: "#e8e8e8",
                                      padding: "5px 10px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    <p
                                      style={{
                                        margin: "0 0 8px 0",
                                        fontSize: "14px",
                                        color: "#718096",
                                        fontWeight: 500,
                                      }}
                                    >
                                      Main Image
                                    </p>
                                    <p
                                      style={{
                                        margin: 0,
                                        fontSize: "16px",
                                        color: "#2d3748",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {image.isreadyimage ? "YES" : "NO"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                      </>
                    )}
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </div>
          )}
        </AppDialog>
      )}
    </>
  );
};

export default DullOrderItemArray;
